import { getPrivacyPolicy, getTermsOfInteractiveApp, getTermsOfService, loadTermsAndPolicies } from '@/api';
import buttonBack from '@/assets/btn/back.png';
import { Device } from '@/classes';
import { keyCodes } from '@/constants/keyCode';
import { PopupContext } from '@/contexts/PopupContext';
import { useExtendFocusable } from '@/hooks/useExtendFocusable';
import { useInputType } from '@/hooks/useInputType';
import { useRouter } from '@/hooks/useRouter';
import { FocusableButton, FocusableImage } from '@/shared/ui';
import { scrollElementTo } from '@/utils';
import { FocusContext, getCurrentFocusKey, setFocus } from '@noriginmedia/norigin-spatial-navigation';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const BUTTON_CLASSES = `border-none hover-bg-primary text-black hover-text-white text-bold outline-disable`;

export const TermsPage = () => {
  const { inputType } = useInputType();
  const { ref, focusKey, focusSelf } = useExtendFocusable();
  const router = useRouter();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentTab, setCurrentTab] = useState<'terms' | 'interactive' | 'privacyPolicy' | 'openSourceLicense'>(
    'terms',
  );
  const [_selected, setSelected] = useState(false);
  const selected = useMemo(() => _selected || inputType === 'MOUSE', [_selected, inputType]);
  const [termsValue, setTermsValue] = useState('');
  const { t, i18n } = useTranslation();
  const popupContext = useContext(PopupContext);
  const longTextCustomSize = useMemo(() => {
    switch (i18n.language) {
      case 'id':
        return '26px';
      case 'es':
      case 'ja':
      case 'pt':
        return '28px';
      default:
        return '34px';
    }
  }, [i18n.language]);

  const onClick = useCallback(() => {
    router.pop();
  }, [router]);

  const initialize = useCallback(async () => {
    if (!getPrivacyPolicy(Device.language)) {
      try {
        await loadTermsAndPolicies();
      } catch {
        if (popupContext) {
          popupContext.showSystemErrorPopup();
        }
      }
    }

    function removeLinks(htmlString: string) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, 'text/html');
      const links = doc.querySelectorAll('a');

      for (const key in links) {
        const link = links[key];
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
      }

      return doc.body.innerHTML;
    }

    switch (currentTab) {
      case 'terms': {
        const html = removeLinks(getTermsOfService(Device.language));
        setTermsValue(html);
        break;
      }
      case 'interactive': {
        const html = removeLinks(getTermsOfInteractiveApp(Device.language));
        setTermsValue(html);
        break;
      }
      case 'privacyPolicy': {
        const html = removeLinks(getPrivacyPolicy(Device.language));
        setTermsValue(html);
        break;
      }
    }

    if (scrollRef.current) {
      scrollElementTo({ element: scrollRef.current, top: 0, behavior: 'auto' });
    }
  }, [currentTab, popupContext]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    focusSelf();
  }, [focusSelf]);

  const handleKeyEvent = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const isPressedCloseButton =
        e.keyCode === 10009 ||
        e.keyCode === 461 ||
        e.code === 'Backspace' ||
        e.code === 'Escape' ||
        e.key === 'Backspace' ||
        e.key === 'Escape';

      if (e.keyCode === keyCodes.ENTER && getCurrentFocusKey().includes('tab')) {
        setSelected(true);
      }
      if (isPressedCloseButton) {
        if (selected) {
          if (getCurrentFocusKey().includes('tab')) {
            setSelected(false);
            e.stopPropagation();
          }
        }
      }
      if (selected) {
        switch (e.keyCode) {
          case keyCodes.UP:
            if (scrollRef?.current) {
              if (scrollRef.current?.scrollBy) {
                scrollRef.current?.scrollBy({ top: -100 });
              } else {
                scrollRef.current.scrollTop -= 100;
              }
            }
            e.stopPropagation();
            break;
          case keyCodes.DOWN:
            if (scrollRef?.current) {
              if (scrollRef.current?.scrollBy) {
                scrollRef.current?.scrollBy({ top: 100 });
              } else {
                scrollRef.current.scrollTop += 100;
              }
            }
            break;
          case keyCodes.LEFT:
          case keyCodes.RIGHT:
            if (selected) {
              if (getCurrentFocusKey().includes('tab')) {
                setSelected(false);
                e.stopPropagation();
              }
            }
            e.stopPropagation();
            break;
        }
      }
    },
    [selected],
  );

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        ref={ref}
        className="flex flex-col h-screen bg-white"
        onKeyDown={handleKeyEvent}
        onKeyDownCapture={handleKeyEvent}
      >
        <header className="bg-[#0F2648] h-[306px] text-[60px] text-white text-bold flex flex-col">
          <div className="flex items-center my-[60px]">
            <FocusableImage
              src={buttonBack}
              className={`ml-[74px] focus:bg-[#FF66AE] hover:bg-[#FF66AE] p-[20px] rounded-[20px] outline-disable`}
              onClick={onClick}
              focusKey="backButton"
            />
            <span className="ml-[61px]">{t('mypage_menu_terms')}</span>
          </div>

          <div className="flex w-[1753px] h-[96px] rounded-t-[20px] bg-[#FFD9E9] mx-auto overflow-hidden">
            <FocusableButton
              className={`w-[583px] mr-[2px] text-[34px] ${
                currentTab === 'terms' && selected
                  ? 'focus:bg-[#FFD9E9] focus:text-black'
                  : 'focus:bg-primary focus:text-white'
              } ${BUTTON_CLASSES} ${currentTab === 'terms' ? 'bg-[#FFD9E9]' : 'bg-[#FFF8FB]'} `}
              onClick={() => {
                setCurrentTab('terms');
                setFocus('tabAll');
              }}
              initFocus={true}
              focusKey="tabAll"
            >
              {t('terms_required_pinkfong')}
            </FocusableButton>

            <FocusableButton
              className={`w-[583px] mr-[2px] ${
                currentTab === 'interactive' && selected
                  ? 'focus:bg-[#FFD9E9] focus:text-black'
                  : 'focus:bg-primary focus:text-white'
              } ${BUTTON_CLASSES} ${currentTab === 'interactive' ? 'bg-[#FFD9E9]' : 'bg-[#FFF8FB]'}`}
              onClick={() => {
                setCurrentTab('interactive');
                setFocus('tabInteractive');
              }}
              focusKey="tabInteractive"
              style={{
                fontSize: longTextCustomSize,
              }}
            >
              {t('terms_required_interactive')}
            </FocusableButton>

            <FocusableButton
              className={`w-[583px] text-[34px] ${
                currentTab === 'privacyPolicy' && selected
                  ? 'focus:bg-[#FFD9E9] focus:text-black'
                  : 'focus:bg-primary focus:text-white'
              } ${BUTTON_CLASSES} ${currentTab === 'privacyPolicy' ? 'bg-[#FFD9E9]' : 'bg-[#FFF8FB]'}`}
              onClick={() => {
                setCurrentTab('privacyPolicy');
                setFocus('tabPrivacyPolicy');
              }}
              focusKey="tabPrivacyPolicy"
            >
              {t('terms_required_privacy')}
            </FocusableButton>
          </div>
        </header>

        <main
          ref={scrollRef}
          className="mr-[30px] pt-[30px] px-[80px] pb-[30px] text-[#1B1B1B] text-[32px] leading-[55px] custom-scrollbar whitespace-pre-wrap transition-none overflow-y-scroll bg-white"
          style={{ overflowWrap: 'break-word', height: '774px' }}
        >
          <div dangerouslySetInnerHTML={{ __html: termsValue }} />
        </main>
      </div>
    </FocusContext.Provider>
  );
};

export default TermsPage;
